import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Seo, breadcrumbLd, faqLd, ORIGIN } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArrowUpRight, Search, MapPin } from "lucide-react";

const FAQS = [
  { q: "Which Wisconsin high schools does Heather's Boutique serve?", a: "Heather's Boutique serves 250+ Wisconsin high schools statewide, from Milwaukee and Madison to Green Bay, Appleton and beyond. Each school has its own exclusive prom collection and dress-lock network." },
  { q: "How do I find prom dresses for my school?", a: "Search for your school below, open your school page, and register with your school email to unlock your school's exclusive collection, dress locks, and squad planning." },
  { q: "What is a school dress lock?", a: "It guarantees no two students at the same school buy the same dress. Lock a dress for 2 hours to reserve it exclusively for you at your school." },
];

export default function Schools() {
  const [schools, setSchools] = useState([]);
  const [counties, setCounties] = useState([]);
  const [countyGeo, setCountyGeo] = useState([]);
  const [cityGeo, setCityGeo] = useState([]);
  const [searchParams] = useSearchParams();
  const cityParam = searchParams.get("city") || "";
  const [q, setQ] = useState(cityParam);
  const [county, setCounty] = useState("all");

  useEffect(() => {
    if (cityParam) setQ(cityParam);
  }, [cityParam]);

  useEffect(() => {
    api.schools().then(({ data }) => setSchools(data));
    api.schoolCounties().then(({ data }) => setCounties(data)).catch(() => {});
    api.geoCounties().then(({ data }) => setCountyGeo(data)).catch(() => {});
    api.geoCities().then(({ data }) => setCityGeo(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return schools.filter((s) => {
      if (county !== "all" && s.county !== county) return false;
      if (q) {
        const t = q.toLowerCase();
        return [s.name, s.city, s.mascot, s.county].some((v) => (v || "").toLowerCase().includes(t));
      }
      return true;
    });
  }, [schools, q, county]);

  const crumbs = [{ name: "Home", path: "/" }, { name: "Schools", path: "/schools" }];

  return (
    <div data-testid="schools-page" className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-12">
      <Seo
        title="Wisconsin High Schools — Prom by School"
        description="Find prom, homecoming & graduation dresses for your WI high school. 250+ schools with school-exclusive collections and dress locks."
        path="/schools"
        jsonLd={[
          breadcrumbLd(crumbs),
          faqLd(FAQS),
          { "@context": "https://schema.org", "@type": "ItemList", name: "Wisconsin High Schools served by Heather's Boutique", numberOfItems: schools.length, itemListElement: schools.slice(0, 50).map((s, i) => ({ "@type": "ListItem", position: i + 1, url: `${ORIGIN}/schools/${s.slug || s.id}`, name: s.name })) },
        ]}
      />
      <Breadcrumbs items={crumbs} />
      <p className="font-label text-[10px] text-rosemetal mb-2">Wisconsin Prom Network</p>
      <h1 className="font-display text-5xl sm:text-6xl mb-4">Find your school</h1>
      <p className="font-body text-sm text-muted-foreground max-w-2xl mb-8">Heather's Boutique serves <b>{schools.length}</b> Wisconsin high schools. Every school gets its own exclusive experience — one dress per student, per school. Verify your school email to unlock locks, squads, and your school's memory hub.</p>

      <div className="flex flex-wrap items-center gap-4 border-y border-border py-5 mb-8">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input data-testid="school-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search school, city or mascot…" className="bg-transparent py-1 text-sm outline-none flex-1" />
        </div>
        <select data-testid="county-filter" value={county} onChange={(e) => setCounty(e.target.value)} className="bg-transparent font-label text-[10px] outline-none cursor-pointer border border-border rounded-sm px-3 py-2">
          <option value="all">All Counties</option>
          {counties.map((c) => <option key={c} value={c}>{c} County</option>)}
        </select>
        <span className="font-label text-[9px] text-muted-foreground">{filtered.length} schools</span>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map((s, i) => (
          <Link key={s.id} to={`/schools/${s.slug || s.id}`} data-testid={`school-${s.id}`} className="group border border-border rounded-sm p-6 hover:border-rosemetal transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-display text-2xl group-hover:text-rosemetal transition-colors leading-tight">{s.name}</h2>
                <p className="font-body text-xs text-muted-foreground mt-1.5">{s.city}{s.county ? `, ${s.county} County` : ""} · {s.mascot || "—"}{s.students ? ` · ${s.students} students` : ""}</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-rosemetal shrink-0" />
            </div>
            <div className="mt-4 h-1 rounded-full" style={{ background: s.primary_color }} />
          </Link>
        ))}
      </div>

      {/* Browse by county — silo internal linking */}
      <section className="mt-20" data-testid="browse-by-county">
        <h2 className="font-display text-4xl mb-2">Browse by county</h2>
        <p className="font-body text-sm text-muted-foreground mb-6">Prom dresses across all {countyGeo.length} Wisconsin counties we serve.</p>
        <div className="flex flex-wrap gap-2">
          {countyGeo.map((c) => (
            <Link key={c.slug} to={`/schools/county/${c.slug}`} data-testid={`county-link-${c.slug}`} className="font-body text-sm border border-border rounded-sm px-3 py-1.5 hover:border-rosemetal hover:text-rosemetal transition-colors">
              {c.name} <span className="text-muted-foreground text-xs">({c.school_count})</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular cities */}
      <section className="mt-16" data-testid="popular-cities">
        <h2 className="font-display text-4xl mb-6">Prom dresses by city</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cityGeo.slice(0, 24).map((c) => (
            <Link key={c.slug} to={`/prom-dresses/city/${c.slug}`} data-testid={`city-link-${c.slug}`} className="group flex items-center gap-2 border border-border rounded-sm px-4 py-3 hover:border-rosemetal transition-colors">
              <MapPin className="w-3.5 h-3.5 text-rosemetal shrink-0" strokeWidth={1.5} />
              <span className="font-body text-sm group-hover:text-rosemetal">{c.name}, WI</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ AEO */}
      <section className="max-w-[900px] mt-24" data-testid="schools-faq">
        <h2 className="font-display text-4xl mb-8">Prom by school — FAQ</h2>
        <div className="divide-y divide-border border-y border-border">
          {FAQS.map((f, i) => (
            <details key={i} className="group py-5">
              <summary className="font-display text-xl cursor-pointer list-none flex justify-between items-center">{f.q}<span className="text-rosemetal text-2xl group-open:rotate-45 transition-transform">+</span></summary>
              <p className="font-body text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
